import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import ModalPopUp from "@/Components/common/ModalPopUp";
import { fetchFiltersData } from "@/reducers/clientscandidates";
import {
  deSelectAllCandidate,
  fetchAuthorisedTimesheets,
  reStateFilters,
  setAuthorisedTimesheetsLoading,
  showFilterModal,
  updateFilters,
  updatePagination,
} from "@/reducers/timesheets/timesheetauthorised";

import CalendarFilter from "../../../ListingFilters/CalendarFilter";
import ComplianceStatusFilter from "../../../ListingFilters/ComplianceStatusFilter";
import DropDownFilter from "../../../ListingFilters/DropDownFilter";
import FilterModalBottomAction from "../../../ListingFilters/FilterModalBottomAction";
import { FilterModalTop } from "../../../ListingFilters/FilterModalTop";
import MilesFilter from "../../../ListingFilters/MilesFilter";
import SessionTypeFilter from "../../../ListingFilters/SessionTypeFilter";
import TextFieldFilter from "../../../ListingFilters/TextFieldFilter";

/**
 * Renders the FilterModal component with various filter options and actions.
 *
 * @return {JSX.Element} The rendered FilterModal component
 */
export const FilterModal = () => {
  const dispatch = useDispatch();
  const { filters, showFilters, pagination } = useSelector(
    (state) => state.clientTimesheetAuthorised,
  );
  const { filterLists } = useSelector((state) => state.clientsCandidates);
  /**
   * Function to handle the closing of the modal.
   *
   * @param {boolean} fromBtn - indicates if the function was called from a button
   * @return {void}
   */
  const handleClose = (fromBtn = false) => {
    if (fromBtn) {
      dispatch(reStateFilters());
    }
    dispatch(showFilterModal(false));
  };
  useEffect(() => {
    dispatch(fetchFiltersData());
  }, []);
  /**
   * This function handles the apply action by dispatching various actions and closing a modal.
   */
  const handleApply = () => {
    dispatch(setAuthorisedTimesheetsLoading(true));
    dispatch(
      updatePagination({
        ...pagination,
        currentPage: 1,
        isLastPage: false,
      }),
    );
    dispatch(deSelectAllCandidate());
    dispatch(fetchAuthorisedTimesheets(filters));
    handleClose();
  };
  /**
   * Function to handle reset.
   */
  const handleReset = () => {
    dispatch(updateFilters({}));
    dispatch(setAuthorisedTimesheetsLoading(true));
    dispatch(
      updatePagination({
        ...pagination,
        currentPage: 1,
        isLastPage: false,
      }),
    );
    dispatch(deSelectAllCandidate());
    dispatch(
      fetchAuthorisedTimesheets({
        search: filters.search,
      }),
    );
    handleClose();
  };
  return (
    <ModalPopUp
      show={showFilters}
      panelClass="max-w-[774px]"
      onClose={() => {
        handleClose(true);
      }}
    >
      <div className="grid gap-6">
        <div className="grid gap-[18px]">
          <div className="grix gap-4">
            <FilterModalTop
              handleClose={() => {
                handleClose(true);
              }}
            />
          </div>
          <div className="grid grid-cols-1 gap-x-6 gap-y-[18px] sm:grid-cols-2">
            <SessionTypeFilter
              active={filters.sessionType}
              handleClick={(value) => {
                dispatch(updateFilters({ sessionType: value }));
              }}
            />
            <div className="grid gap-3">
              <label className="label text-black" htmlFor="contractReferenceNo">
                Contract Reference No.
              </label>
              <input
                type="text"
                className="ease h-[40px] w-full rounded-lg border border-lightGrey px-4 text-[14px] font-medium leading-[normal] text-black transition-all duration-200 placeholder:font-medium placeholder:text-txtColor focus:border-primary"
                placeholder="Enter Reference No."
                onChange={(e) => {
                  dispatch(
                    updateFilters({ contractReferenceNo: e.target.value }),
                  );
                }}
                value={filters.contractReferenceNo}
                onKeyUp={(e) => {
                  if (e.keyCode === 13) {
                    handleApply();
                  }
                }}
              />
            </div>
            <DropDownFilter
              options={
                filterLists && filterLists?.allCandidates?.length > 0
                  ? filterLists?.allCandidates
                  : []
              } // this is the list of candidates
              id="candidateId"
              label="Candidate"
              placeholder="Select Candidate"
              value={filters.candidateId} // selected candidate
              handleChange={(value) => {
                dispatch(updateFilters({ candidateId: value }));
              }} // function to handle change of candidate
              isMultiple={true}
            />
            <TextFieldFilter
              handleKeyUp={(e) => {
                if (e.keyCode === 13) {
                  handleApply();
                }
              }}
              handleChange={(value) => {
                dispatch(updateFilters({ candidateReferenceNo: value }));
              }}
              label="Candidate Reference No."
              value={filters.candidateReferenceNo}
              filterId="candidateReferenceNo"
              placeholder="Enter Reference No."
            />
            <TextFieldFilter
              handleKeyUp={(e) => {
                if (e.keyCode === 13) {
                  handleApply();
                }
              }}
              handleChange={(value) => {
                dispatch(updateFilters({ candidateLicenseNo: value }));
              }}
              label="Prof. Registration No."
              value={filters.candidateLicenseNo}
              filterId="candidateLicenseNo"
              placeholder="GMC/NMC/GPhC."
            />
            <ComplianceStatusFilter
              value={filters.complianceStatus}
              handleChange={(value) => {
                dispatch(updateFilters({ complianceStatus: value }));
              }}
            />
            <DropDownFilter
              options={
                filterLists && filterLists?.allRoles?.length > 0
                  ? filterLists?.allRoles
                  : []
              } // this is the list of roles
              id="candidateRole"
              label="Role"
              placeholder="Select Role"
              value={filters.candidateRole} // selected role
              handleChange={(value) => {
                dispatch(updateFilters({ candidateRole: value }));
              }} // function to handle change of role
            />
            <MilesFilter
              value={filters.miles}
              handleChange={(value) => {
                dispatch(updateFilters({ miles: value }));
              }}
            />
            <DropDownFilter
              options={
                filterLists && filterLists?.allUsers?.length > 0
                  ? filterLists?.allUsers.filter(
                      (user) => user.type !== "candidate",
                    )
                  : []
              } // this is the list of roles
              id="Authorised By"
              label="Authorised By"
              // isMultiple="false"
              placeholder="Select Authorised by"
              value={filters.actionedById} // selected role
              handleChange={(value, type) => {
                dispatch(
                  updateFilters({ actionedById: value, actionedByType: type }),
                );
              }} // function to handle change of role
            />
          </div>
          <div className="_filter_calendar">
            <CalendarFilter
              label="Date Worked (From - To)"
              dateFrom={filters.workedDateFrom}
              dateTo={filters.workedDateTo}
              handleChange={(value) => {
                dispatch(
                  updateFilters({
                    workedDateFrom: value.dateFrom,
                    workedDateTo: value.dateTo,
                  }),
                );
              }}
            />
          </div>
          <div className="_filter_calendar">
            <CalendarFilter
              label="Shift Date (From - To)"
              dateFrom={filters.contractDateFrom}
              dateTo={filters.contractDateTo}
              handleChange={(value) => {
                dispatch(
                  updateFilters({
                    contractDateFrom: value.dateFrom,
                    contractDateTo: value.dateTo,
                  }),
                );
              }}
            />
          </div>
        </div>
        <FilterModalBottomAction
          applyFunc={handleApply}
          resetFunc={handleReset}
        />
      </div>
    </ModalPopUp>
  );
};
